<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable">
      <ui-chips v-model="orderStatus" :options="statusChips" :label="$t('order_status')" />
    </report-tools>

    <report-state
      v-if="!selectedCompany"
      icon="mdi-domain"
      :title="$t('report_mobile.employees_pick_company')"
    ></report-state>
    <report-state v-else-if="loading && !orderUsers.length" kind="loading"></report-state>
    <report-state v-else-if="!rows.length" icon="mdi-account-off-outline" :title="emptyText"></report-state>

    <ui-table
      v-else
      variant="report"
      wrap-class="rd-card--scroll"
      table-class="rd-tbl--workers"
      :columns="columns"
      :rows="rows"
      :row-key="(row) => row.key"
      :sort="sort"
      :totals="sums"
      :total-label="$t('total')"
      :selected="isOpen"
      :row-class="workerRowClass"
      @sort="sortBy"
      @open="openWorker"
    >
      <template #head-shift="{ column }">
        <report-shift-tag :shift="column.shift.id"></report-shift-tag>
      </template>

      <template #cell-name="{ row }">
        <span class="rd-person">
          <span class="ui-avatar ui-avatar--neutral ui-avatar--sm">{{ row.initials }}</span>
          <span class="rd-person__name">{{ row.name }}</span>
        </span>
      </template>

      <template #cell-shift="{ row, column }">
        <span v-if="row[column.key]" class="rs-tag rs-tag--count" :data-shift="column.shift.id">
          {{ row[column.key] }}
          <span class="rs-tag__sr">{{ $t("report_mobile.shift_n", { n: column.shift.id }) }}</span>
        </span>
        <template v-else>
          <span class="rd-dash" aria-hidden="true">–</span>
          <span class="rd-visually-hidden">0</span>
        </template>
      </template>

      <template #cell-status="{ row }">
        <span class="rd-status" :class="`rd-status--${row.state}`">{{ row.statusLabel }}</span>
      </template>
    </ui-table>
  </div>
</template>

<script>
import ReportTabGroupMealsTabEmployees from "@/components/report/group-meals/ReportTabGroupMealsTabEmployees.vue";
import { SHIFTS } from "@/constants/shifts";
import ReportShiftTag from "../../../shared/ReportShiftTag.vue";
import hasGroupUserRows from "../../../shared/group/hasGroupUserRows";
import { copyText, rowsToTsv } from "../../copyTable";
import hasTableSort from "../../hasTableSort";
import ReportState from "../../ReportState.vue";
import ReportTools from "../../ReportTools.vue";
import ReportDesktopWorkerMonth from "../ReportDesktopWorkerMonth.vue";

/**
 * Korisnici firme (grupe) na računaru: spisak svih radnika sa smenom u kojoj su poručili, poručeno / preuzeto /
 * razlikom i statusom; filter statusa su čipovi sa brojevima. Klik na red otvara mesečni pregled radnika u panelu.
 * Firmu i period čita iz zajedničke trake, a ekran ne filtrira po smenama (smena je u svakom redu, kao i ranije).
 * Nasleđuje učitavanje, filter statusa, štampu i Excel iz starog taba.
 */
export default {
  name: "ReportDesktopGroupUsers",
  extends: ReportTabGroupMealsTabEmployees,
  mixins: [hasGroupUserRows, hasTableSort],
  components: { ReportShiftTag, ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "id", label: "ID", cls: "rd-col-id", num: true, muted: true, sortable: true },
        { key: "name", label: this.$t("worker"), name: true, title: (row) => row.title, sortable: true },
        ...(this.isSingleDay
          ? [{ key: "time", label: this.$t("time"), cls: "rd-col-time", headTitle: this.$t("order_datetime"), num: true, muted: true, sortKey: "sortTime", sortable: true, value: (row) => row.time || "–" }]
          : []),
        ...this.visibleShifts.map((shift) => ({ key: shift.key, slot: "shift", shift, align: "center", sortable: true })),
        { key: "ordered", label: this.$t("ordered"), align: "end", num: true, zero: true, sortable: true },
        { key: "taken", label: this.$t("taken"), cls: "rd-col-taken", align: "end", num: true, zero: true, sortable: true },
        {
          key: "difference",
          label: this.$t("difference"),
          align: "end",
          num: true,
          zero: (row) => row.difference === 0,
          sortable: true,
          cellClass: (row) => ({ "is-alert": row.difference > 0 }),
        },
        { key: "status", label: this.$t("status"), sortKey: "stateRank", sortable: true },
        { key: "go", label: "", go: true },
      ];
    },
    /** Smene 1–3 uvek, četvrta samo kad neko u njoj ima narudžbu. */
    visibleShifts() {
      return SHIFTS.filter((shift) => shift.id < 4 || this.users.some((user) => Number(user[shift.key]) > 0));
    },
    rows() {
      return this.sortRows(this.matchedRows);
    },
    sums() {
      const sums = { ordered: 0, taken: 0, difference: 0 };
      SHIFTS.forEach((shift) => {
        sums[shift.key] = 0;
      });
      this.rows.forEach((row) => {
        sums.ordered += row.ordered;
        sums.taken += row.taken;
        sums.difference += row.difference;
        SHIFTS.forEach((shift) => {
          sums[shift.key] += row[shift.key];
        });
      });
      return sums;
    },
    kpis() {
      return [
        { key: "ordered", label: this.$t("ordered"), value: this.sums.ordered, tone: "inverse" },
        { key: "taken", label: this.$t("taken"), value: this.sums.taken },
        { key: "difference", label: this.$t("difference"), value: this.sums.difference },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    workerRowClass(row) {
      return { "is-idle": row.state === "off" };
    },
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `worker-${row.id}`);
    },
    /** Mesečni pregled radnika u panelu (isti podaci kao stari dijalog: "food-orders-by-month"). */
    openWorker(row, event) {
      const company = this.selectedCompany;
      const companyName = this.toLatin(company?.company || company?.name || "");
      this.$emit("open-panel", {
        opener: event?.currentTarget,
        key: `worker-${row.id}`,
        root: true,
        wide: true,
        title: row.name,
        subtitle: [`ID ${row.id}`, companyName].filter(Boolean).join(" · "),
        component: ReportDesktopWorkerMonth,
        props: {
          compact: true,
          worker: this.toEmployeeContext(row),
        },
      });
    },
    async copyTable() {
      const headers = [
        "ID",
        this.$t("worker"),
        ...(this.isSingleDay ? [this.$t("time")] : []),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("ordered"),
        this.$t("taken"),
        this.$t("difference"),
        this.$t("status"),
      ];
      const rows = this.rows.map((row) => [
        row.id,
        row.name,
        ...(this.isSingleDay ? [row.time] : []),
        ...this.visibleShifts.map((shift) => row[shift.key]),
        row.ordered,
        row.taken,
        row.difference,
        row.statusLabel,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
